import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft, RefreshCw, Trash2, Activity } from "lucide-react";

const STORAGE_KEY = "cbb_event_log";
const ADMIN_TOKEN_KEY = "cbb_admin_token";

// Same admin login flow as the main /admin route — gate this page behind the
// admin token so we don't leak debugging info publicly.
function AnalyticsLogin({ onAuth }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const r = await fetch(`${apiBase}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      if (!r.ok) {
        setErr("Wrong password.");
        return;
      }
      const j = await r.json();
      try { sessionStorage.setItem(ADMIN_TOKEN_KEY, j.token); } catch (_e) { /* private-mode */ }
      onAuth(j.token);
    } catch (_e) {
      setErr("Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} className="card p-8 w-full max-w-sm" data-testid="analytics-login">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
            <Activity size={18} strokeWidth={2.5} />
          </div>
          <div className="font-display text-xl">Analytics Debug</div>
        </div>
        <input
          type="password"
          placeholder="Admin password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          className="w-full"
          data-testid="analytics-pw"
        />
        {err && <div className="text-xs mt-2" style={{ color: "#ef4444" }}>{err}</div>}
        <button type="submit" disabled={busy} className="btn-primary mt-5 w-full justify-center" data-testid="analytics-login-btn">
          {busy ? "Checking..." : "Unlock"}
        </button>
        <Link to="/" className="text-xs mt-4 inline-flex items-center gap-1 hover:text-white transition" style={{ color: "var(--muted)" }}>
          <ArrowLeft size={12} /> Back to site
        </Link>
      </form>
    </div>
  );
}

function relativeTime(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function eventBadgeColor(eventName) {
  if (!eventName) return "var(--muted)";
  if (eventName === "purchase") return "#22c55e";
  if (eventName === "begin_checkout" || eventName === "sign_up") return "#f59e0b";
  if (eventName === "generate_lead") return "#3b82f6";
  if (eventName === "select_plan" || eventName === "begin_signup") return "var(--accent)";
  if (eventName.startsWith("gtm.")) return "var(--muted)";
  return "rgba(255,255,255,0.7)";
}

function AnalyticsDashboard({ token, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("app"); // app | all
  const [expanded, setExpanded] = useState({});
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    try {
      const buf = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      setEntries(buf.slice().reverse()); // newest first
    } catch (_e) {
      // storage unavailable or corrupted — fall back to empty
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { load(); setTick((t) => t + 1); }, 2000);
    return () => clearInterval(id);
  }, [load]);

  const clear = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* storage unavailable */ }
    setEntries([]);
  };

  const fireTest = () => {
    if (typeof window.cbbTrack === "function") {
      window.cbbTrack("debug_smoke_test", { source: "analytics-debug-page", value: 1, currency: "AUD", ts: Date.now() });
    }
  };

  const shown = entries.filter((e) => {
    if (filter === "all") return true;
    // App events only — exclude noisy GTM/gtag internals
    if (!e.event || e.event.startsWith("gtm.") || e.event === "_internal") return false;
    return true;
  });

  // Stats summary
  const summary = entries.reduce((acc, e) => {
    if (!e.event || e.event.startsWith("gtm.") || e.event === "_internal") return acc;
    acc[e.event] = (acc[e.event] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen px-5 sm:px-8 py-8" data-testid="analytics-debug-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
              <Activity size={18} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-2xl">Analytics Debug</div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>
                Last {entries.length} dataLayer events captured on this browser. Refreshes every 2s.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="text-xs hover:text-white transition" style={{ color: "var(--muted)" }}>← Admin</Link>
            <button onClick={onLogout} className="text-xs hover:text-white transition" style={{ color: "var(--muted)" }}>Sign out</button>
          </div>
        </div>

        {/* Summary bar */}
        {Object.keys(summary).length > 0 && (
          <div className="card p-4 mt-4 flex flex-wrap gap-3 text-xs">
            {Object.entries(summary).map(([ev, n]) => (
              <div key={ev} className="px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--line)" }}>
                <span style={{ color: eventBadgeColor(ev) }}>●</span>{" "}
                <strong>{ev}</strong> × {n}
              </div>
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <button
            onClick={() => setFilter(filter === "app" ? "all" : "app")}
            className="btn-secondary text-xs"
            data-testid="analytics-filter-toggle"
          >
            {filter === "app" ? "Show all (incl. gtm.*)" : "Hide gtm.* events"}
          </button>
          <button onClick={load} className="btn-secondary text-xs" data-testid="analytics-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={fireTest} className="btn-secondary text-xs" data-testid="analytics-fire-test">
            Fire test event
          </button>
          <button onClick={clear} className="btn-secondary text-xs" data-testid="analytics-clear">
            <Trash2 size={12} /> Clear log
          </button>
        </div>

        {/* Events */}
        <div className="mt-5 space-y-2" data-testid="analytics-event-list">
          {shown.length === 0 && (
            <div className="card p-8 text-center text-sm" style={{ color: "var(--muted)" }}>
              No events captured yet. Browse the site (try clicking a pricing card or submitting the contact form), then come back.
            </div>
          )}
          {shown.map((e, idx) => {
            const id = `${e.ts}-${idx}`;
            const isOpen = !!expanded[id];
            return (
              <div key={id} className="card p-4">
                <div
                  className="flex items-start justify-between gap-3 cursor-pointer select-none"
                  onClick={() => setExpanded((x) => ({ ...x, [id]: !x[id] }))}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm" style={{ color: eventBadgeColor(e.event) }}>
                      {e.event}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                      <span title={new Date(e.ts).toISOString()}>{relativeTime(e.ts)}</span>
                      <span className="mx-2">·</span>
                      <span className="font-mono">{e.path}</span>
                    </div>
                  </div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>{isOpen ? "▾" : "▸"}</div>
                </div>
                {isOpen && (
                  <pre
                    className="mt-3 text-xs overflow-x-auto p-3 rounded"
                    style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--line)", color: "rgba(255,255,255,0.85)" }}
                  >
                    {JSON.stringify(e.data, null, 2)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
          <strong>How this works:</strong> the site wraps <code>window.dataLayer.push</code> to mirror
          every event into <code>localStorage</code> ({STORAGE_KEY}, capped at 50 entries). This page
          renders them so you can verify event names + payloads without GTM Preview mode. Events are
          captured per-browser and are private to this device. Cleared on demand or when localStorage
          is wiped.
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsDebugPage() {
  const [token, setToken] = useState(null);

  useEffect(() => {
    let t = null;
    try { t = sessionStorage.getItem(ADMIN_TOKEN_KEY); } catch (_e) { /* private-mode / storage disabled */ }
    if (t) setToken(t);
    // Set page title
    const prev = document.title;
    document.title = "Analytics Debug | Cellblock B";
    return () => { document.title = prev; };
  }, []);

  const logout = () => {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch (_e) { /* private-mode / storage disabled */ }
    setToken(null);
  };

  if (!token) return <AnalyticsLogin onAuth={setToken} />;
  return <AnalyticsDashboard token={token} onLogout={logout} />;
}
